import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { RepliconDialog } from '~/modules/common/components';
import TaskEstimateRowContextProvider from '../../TaskEstimateRowContextProvider';
import {
  RemoveTaskEstimateConfirmationDialogTitle,
  RemoveTaskEstimateConfirmationDialogContent
} from '../RemoveTaskEstimateConfirmationDialog';
import { useTaskEstimateRowContextDataBuilder } from '../../hooks';
import RemoveTaskAssignmentDialogContentSkeleton from './RemoveTaskAssignmentDialogContentSkeleton';
import { RemoveTaskAssignmentConfirmationDialogFooter } from './RemoveTaskAssignmentConfirmationDialogFooter';

const useDialogStyles = makeStyles(theme => ({
  paper: {
    maxWidth: theme.spacing(90)
  }
}));

export const RemoveTaskAssignmentConfirmationDialog = ({
  projectId,
  task,
  user,
  open,
  onClose,
  onRemoveTaskAssignmentSuccess,
  refetchQueries,
  canViewCost = false,
  updateCache
}) => {
  const dialogClasses = useDialogStyles();

  const {
    loading,
    taskDetail,
    taskResourceEstimate
  } = useTaskEstimateRowContextDataBuilder({
    projectId,
    task,
    user,
    includeTaskResourceUserCostProgressSummary: canViewCost
  });

  const updateCacheCallback = updateCache
    ? updateCache({
        userId: user.id,
        projectId,
        taskUris: [task.id]
      })
    : undefined;

  return (
    <TaskEstimateRowContextProvider
      task={taskDetail}
      resourceEstimate={taskResourceEstimate}
      canEditTasks={false}
      canViewCost={canViewCost}
    >
      <RepliconDialog
        classes={dialogClasses}
        fullWidth
        open={open}
        onClose={onClose}
        aria-labelledby="remove-task-estimate-confirmation-dialog-title"
        aria-describedby="remove-task-estimate-confirmation-dialog-description"
      >
        <RemoveTaskEstimateConfirmationDialogTitle
          taskName={task.displayText}
          isParentTask={task.hasChildren}
        />
        {loading && <RemoveTaskAssignmentDialogContentSkeleton />}
        {!loading && (
          <RemoveTaskEstimateConfirmationDialogContent
            resourceEstimate={taskResourceEstimate}
            task={task}
          />
        )}
        <RemoveTaskAssignmentConfirmationDialogFooter
          onClose={onClose}
          onRemoveTaskAssignmentSuccess={onRemoveTaskAssignmentSuccess}
          isLoading={loading}
          refetchQueries={refetchQueries}
          updateCache={updateCacheCallback}
        />
      </RepliconDialog>
    </TaskEstimateRowContextProvider>
  );
};

RemoveTaskAssignmentConfirmationDialog.propTypes = {
  projectId: PropTypes.string.isRequired,
  task: PropTypes.object,
  user: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRemoveTaskAssignmentSuccess: PropTypes.func.isRequired,
  refetchQueries: PropTypes.array,
  canViewCost: PropTypes.bool,
  updateCache: PropTypes.func
};
